import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, MapPin, Calendar, Tag, MessageCircle, Loader2, ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import { api, fileUrl, formatPrice } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Seo } from "@/components/Seo";
import { ListingTypeBadge } from "@/components/ListingTypeBadge";
import { EnquiryModal } from "@/components/EnquiryModal";
import { toast } from "sonner";

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return url;
}

export default function MachineDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [enquire, setEnquire] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/machines/slug/${slug}`)
      .then((r) => {
        setM(r.data);
        setActive(0);
        const key = `mashiniya_mview_${r.data.id}_${new Date().toISOString().slice(0, 10)}`;
        if (!sessionStorage.getItem(key)) {
          api.post(`/machines/${r.data.id}/view`).catch(() => {}).finally(() => {
            try { sessionStorage.setItem(key, "1"); } catch (e) { /* ignore */ }
          });
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const directWhatsapp = useCallback(async () => {
    try {
      const { data } = await api.get(`/machines/${m.id}/whatsapp`);
      if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank");
    } catch {
      toast.error("Seller contact unavailable");
    }
  }, [m]);

  if (notFound) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-4 py-32 text-center">
          <h1 className="font-display text-3xl font-bold text-slate-900">Machine not found</h1>
          <p className="mt-2 text-slate-500">This listing may have been removed or is no longer public.</p>
          <Link to="/used-machines" className="mt-6 inline-flex rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">Browse Machines</Link>
        </div>
      </Layout>
    );
  }

  if (!m) return <Layout><div className="flex justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div></Layout>;

  const images = m.images || [];
  const isSold = m.status === "sold";
  const isSpare = m.listing_type === "spare";
  const primaryLabel = isSpare ? "REQUEST QUOTE" : "ENQUIRE NOW";
  const videos = (m.videos || []).filter(Boolean);
  const specs = m.specs || [];

  const prev = () => setActive((a) => (a - 1 + images.length) % images.length);
  const next = () => setActive((a) => (a + 1) % images.length);

  const infoRows = [
    ["Category", m.category_name], ["Brand", m.brand], ["Model", m.model],
    ["Year", m.year], ["Condition", m.condition],
    ["Part Number", m.part_number], ["Compatible Machine", m.compatible_machine], ["Compatible Brand", m.compatible_brand],
    ["Warranty", m.warranty], ["Availability", m.availability],
    ["Location", [m.city, m.state].filter(Boolean).join(", ")],
  ].filter(([, v]) => v);

  return (
    <Layout>
      <Seo
        title={`${m.name}${m.brand ? " — " + m.brand : ""} | Mashiniya`}
        description={(m.description || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 155) || `${m.name} available on Mashiniya${[m.city, m.state].filter(Boolean).length ? " in " + [m.city, m.state].filter(Boolean).join(", ") : ""}. Enquire directly with the seller.`}
        image={images[0] ? fileUrl(images[0].url) : undefined}
        type="product"
      />
      <div className="mx-auto max-w-7xl px-4 pb-28 pt-6 sm:px-6 lg:px-8 lg:pb-12">
        <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="grid gap-8 lg:grid-cols-5">
          {/* GALLERY */}
          <div className="lg:col-span-3">
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
              {images[active] && (
                <img
                  data-testid="gallery-main-image"
                  src={fileUrl(images[active].url)}
                  alt={m.name}
                  className="h-full w-full cursor-zoom-in object-cover"
                  onClick={() => setLightbox(true)}
                />
              )}
              <div className="absolute left-4 top-4"><StatusBadge status={m.status} big /></div>
              {isSold && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <span className="rotate-[-8deg] rounded-lg border-4 border-red-500 px-8 py-2 font-display text-5xl font-extrabold uppercase tracking-widest text-red-500">Sold</span>
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow"><ChevronLeft size={20} /></button>
                  <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow"><ChevronRight size={20} /></button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto hide-scrollbar">
                {images.map((img, i) => (
                  <button
                    key={img.id || i}
                    data-testid={`thumbnail-${i}`}
                    onClick={() => setActive(i)}
                    className={`h-20 w-24 shrink-0 overflow-hidden rounded-lg border-2 ${i === active ? "border-[hsl(var(--brand))]" : "border-transparent"}`}
                  >
                    <img src={fileUrl(img.url)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* SPECS */}
            {specs.length > 0 && (
              <div className="mt-8">
                <h2 className="mb-4 font-display text-xl font-bold text-slate-900">Specifications</h2>
                <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-2">
                  {specs.map((s, i) => (
                    <div key={i} className="flex justify-between bg-white px-4 py-3">
                      <span className="text-sm text-slate-500">{s.name}</span>
                      <span className="text-sm font-semibold text-slate-900">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DESCRIPTION */}
            {m.description && (
              <div className="mt-8">
                <h2 className="mb-4 font-display text-xl font-bold text-slate-900">Description</h2>
                <div className="rich-text text-slate-600" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.description) }} />
              </div>
            )}

            {/* VIDEOS */}
            {videos.length > 0 && (
              <div className="mt-8">
                <h2 className="mb-4 font-display text-xl font-bold text-slate-900">Videos</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {videos.map((v, i) => (
                    <div key={`${v}-${i}`} className="aspect-video overflow-hidden rounded-xl border border-slate-200 bg-black">
                      <iframe src={ytEmbed(v)} title={`Video ${i + 1}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" className="h-full w-full" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* INFO */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24">
              <span className="font-mono text-sm font-semibold text-slate-400">{m.machine_code}</span>
              <div className="mt-2 flex items-center gap-2">
                <ListingTypeBadge type={m.listing_type} />
                {isSold && <StatusBadge status="sold" />}
              </div>
              <h1 className="mt-2 font-display text-2xl font-extrabold leading-tight text-slate-900 sm:text-3xl">{m.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                {m.year && <span className="inline-flex items-center gap-1"><Calendar size={14} />{m.year}</span>}
                {(m.city || m.state) && <span className="inline-flex items-center gap-1"><MapPin size={14} className="brand-accent" />{[m.city, m.state].filter(Boolean).join(", ")}</span>}
              </div>

              <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Price</p>
                <p className="font-display text-3xl font-extrabold text-slate-900">{formatPrice(m.price, m.price_type)}</p>
                <span className="text-sm text-slate-500">{m.price_type}</span>
              </div>

              <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
                {infoRows.map(([k, v], i) => (
                  <div key={k} className={`flex justify-between px-4 py-3 ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                    <span className="text-sm text-slate-500">{k}</span>
                    <span className="text-sm font-semibold text-slate-900">{v}</span>
                  </div>
                ))}
              </div>

              {/* ENQUIRY */}
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
                <h3 className="font-display text-lg font-bold text-slate-900">{isSpare ? "Interested in this spare part?" : "Interested in this machine?"}</h3>
                {isSold ? (
                  <div className="mt-4 rounded-xl bg-red-50 py-4 text-center">
                    <p className="font-display text-2xl font-extrabold text-red-600">SOLD</p>
                    <p className="mt-1 text-sm text-red-500">This {isSpare ? "spare part" : "machine"} has already been sold.</p>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    <button
                      data-testid="detail-enquire-btn"
                      onClick={() => setEnquire(true)}
                      className="w-full rounded-full bg-brand py-3 font-bold text-white"
                    >
                      {primaryLabel}
                    </button>
                    <button
                      data-testid="detail-whatsapp-btn"
                      onClick={directWhatsapp}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-whatsapp py-3 font-bold text-white"
                    >
                      <MessageCircle size={18} /> WHATSAPP ENQUIRY
                    </button>
                    <p className="text-center text-xs text-slate-400">Free enquiry • Seller contact is private</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* STICKY MOBILE BAR */}
      {!isSold && (
        <div className="fixed bottom-0 left-0 right-0 z-40 flex gap-2 border-t border-slate-200 bg-white/95 p-3 backdrop-blur lg:hidden">
          <button data-testid="sticky-enquire-btn" onClick={() => setEnquire(true)} className="flex-1 rounded-full bg-brand py-3 text-sm font-bold text-white">{primaryLabel}</button>
          <button data-testid="sticky-whatsapp-btn" onClick={directWhatsapp} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-whatsapp py-3 text-sm font-bold text-white">
            <MessageCircle size={16} /> WHATSAPP
          </button>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95" onClick={() => setLightbox(false)}>
          <button className="absolute right-5 top-5 text-white" onClick={() => setLightbox(false)}><X size={28} /></button>
          <img src={fileUrl(images[active]?.url)} alt="" className="max-h-[90vh] max-w-[92vw] object-contain" onClick={(e) => e.stopPropagation()} />
          {images.length > 1 && (
            <>
              <button className="absolute left-5 text-white" onClick={(e) => { e.stopPropagation(); prev(); }}><ChevronLeft size={36} /></button>
              <button className="absolute right-5 text-white" onClick={(e) => { e.stopPropagation(); next(); }}><ChevronRight size={36} /></button>
            </>
          )}
        </div>
      )}

      <EnquiryModal machine={enquire ? m : null} open={enquire} onClose={() => setEnquire(false)} />
    </Layout>
  );
}
